import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye } from 'lucide-react'
import { formSubmissionsApi } from '../../../services/api'
import LoadingSpinner from '../../../components/ui/LoadingSpinner'
import Button from '../../../components/ui/Button'
import Card from '../../../components/ui/Card'
import { formatDateTime } from '../../../utils/date'
import { useMarkNotificationsRead } from '../../../hooks/useMarkNotificationsRead'
import { CONTACT_SUBJECT_KEYS, CONTACT_BRANCH_KEYS } from '../../../constants/contact'

const STATUSES = ['new', 'reviewed', 'replied', 'archived']

function formatSubject(subject, t) {
  if (!subject) return '—'
  if (CONTACT_SUBJECT_KEYS.includes(subject)) {
    return t(`contact.form.subjects.${subject}`)
  }
  return subject
}

function formatBranch(branch, t) {
  if (!branch) return '—'
  if (CONTACT_BRANCH_KEYS.includes(branch)) {
    return t(`contact.form.branches.${branch}`)
  }
  return branch
}

export default function AdminContactSubmissions() {
  const { t } = useTranslation()
  useMarkNotificationsRead('forms_contact')
  const [items, setItems] = useState([])
  const [meta, setMeta] = useState(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)

  const load = (pageNum = page) => {
    setLoading(true)
    formSubmissionsApi
      .getAll({ type: 'contact', page: pageNum, per_page: 15 })
      .then(({ data }) => {
        setItems(data.data || [])
        setMeta(data.meta || null)
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load(page)
  }, [page])

  const updateStatus = async (id, status) => {
    await formSubmissionsApi.updateContact(id, { status })
    load(page)
  }

  return (
    <div>
      {loading ? (
        <LoadingSpinner className="py-20" />
      ) : (
        <>
          <Card padding={false} className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-border bg-primary/5">
                <tr className="text-start">
                  <th className="px-4 py-3">{t('admin.forms.reference')}</th>
                  <th className="px-4 py-3">{t('admin.forms.name')}</th>
                  <th className="px-4 py-3">{t('admin.forms.subject')}</th>
                  <th className="px-4 py-3">{t('admin.forms.branch')}</th>
                  <th className="px-4 py-3">{t('admin.forms.status')}</th>
                  <th className="px-4 py-3">{t('admin.forms.date')}</th>
                  <th className="px-4 py-3">{t('admin.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-border/60 align-top">
                    <td className="px-4 py-3">{item.reference_number || item.id}</td>
                    <td className="px-4 py-3">{item.name}</td>
                    <td className="px-4 py-3">{formatSubject(item.subject, t)}</td>
                    <td className="px-4 py-3">{formatBranch(item.branch, t)}</td>
                    <td className="px-4 py-3">
                      <select
                        value={item.status || 'new'}
                        onChange={(e) => updateStatus(item.id, e.target.value)}
                        className="rounded-lg border border-border px-2 py-1 text-xs capitalize"
                      >
                        {STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">{formatDateTime(item.created_at)}</td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/cp-login/forms/contact/${item.id}`}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-primary transition-colors hover:bg-primary/5"
                        aria-label={t('admin.forms.view')}
                        title={t('admin.forms.view')}
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {meta && meta.last_page > 1 && (
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button variant="outlineDark" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                {t('admin.pagination.prev')}
              </Button>
              <span className="text-sm text-text/60">
                {page} / {meta.last_page}
              </span>
              <Button
                variant="outlineDark"
                size="sm"
                disabled={page >= meta.last_page}
                onClick={() => setPage(page + 1)}
              >
                {t('admin.pagination.next')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
